import { supabase } from '@/integrations/supabase/client';
import {
  directPathError,
  getStaffSessionToken,
  handleInvalidSession,
  isInvalidSessionError,
} from '@/services/staffSession';

/**
 * Expected-vs-arrived reconciliation row, one per referred case.
 *
 * Read-only: sourced from the SECURITY DEFINER RPC
 * `get_referral_reconciliation`, which matches every patient_cases row against
 * the accumulated Plato queue history (plato_visits) and buckets it. The RPC
 * performs no writes.
 */
export type ReconciliationBucket =
  | 'arrived'
  | 'booked'
  | 'never_booked'
  | 'awaiting'
  | 'did_not_proceed'
  | 'referred_back';

export interface ReferralReconciliationRow {
  case_id: string;
  patient_display_name: string;
  plato_reference_id: string | null;
  referring_doctor: string | null;
  referring_clinic: string | null;
  status: string;
  referral_received_date: string | null;
  next_action_due_date: string | null;
  last_visit_date: string | null;
  last_visit_status: string | null;
  visit_count: number;
  is_manual: boolean;
  bucket: ReconciliationBucket;
}

export const getReferralReconciliation = async (
  staleDays = 14
): Promise<ReferralReconciliationRow[]> => {
  const callRpc = async (fn: string, args: Record<string, unknown>) =>
    await (supabase.rpc as unknown as (
      name: string,
      params: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { code?: string; message: string } | null }>)(fn, args);

  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    // get_referral_reconciliation reads patient_display_name straight out of
    // patient_cases and is SECURITY DEFINER granted to anon, so it is a second
    // door into the same data that revoking the table does not close (migration
    // 20260930121000 shuts it). This wrapper requires a staff session, and
    // returns the whole result as one JSON array rather than a row per case, so
    // PostgREST's 1,000-row cap cannot silently truncate the tab.
    const { data, error } = await callRpc('get_referral_reconciliation_by_session', {
      p_session_token: sessionToken,
      p_stale_days: staleDays,
    });

    if (error) {
      if (isInvalidSessionError(error)) {
        handleInvalidSession();
      }
      throw error;
    }

    return (data ?? []) as unknown as ReferralReconciliationRow[];
  }

  // Fallback for a browser with no session token; see the note in
  // patientCaseService. Removed once Phase 3 is applied.
  const { data, error } = await callRpc('get_referral_reconciliation', {
    stale_days: staleDays,
  });

  if (error) {
    throw directPathError(error);
  }

  return (data ?? []) as unknown as ReferralReconciliationRow[];
};
